import React, { useState } from 'react'
import { X as CloseIcon, Twitter, Facebook, Linkedin, Instagram, Share2, Download, Copy, Check } from 'lucide-react'
import { Todo } from '../../lib/supabase'
import {
  shareTaskCompletionToTwitter,
  shareToFacebook,
  shareToLinkedIn,
  shareViaWebShareAPI,
  copyToClipboard
} from '../../services/socialShare'
import {
  generateTaskCompletionImage,
  generateInstagramStory,
  downloadImage
} from '../../services/imageGenerator'

interface ShareModalProps {
  isOpen: boolean
  onClose: () => void
  todo: Todo
  shareType?: 'task_completion' | 'progress_update'
}

export const ShareModal: React.FC<ShareModalProps> = ({
  isOpen,
  onClose,
  todo
}) => {
  const [selectedTemplate, setSelectedTemplate] = useState<'minimal' | 'colorful' | 'dark'>('colorful')
  const [generatedImage, setGeneratedImage] = useState<string | null>(null)
  const [isGenerating, setIsGenerating] = useState(false)
  const [copied, setCopied] = useState(false)

  if (!isOpen) return null

  const handleGenerateImage = async () => {
    setIsGenerating(true)
    try {
      const imageUrl = await generateTaskCompletionImage(todo, { template: selectedTemplate })
      setGeneratedImage(imageUrl)
    } catch (error) {
      console.error('Image generation failed:', error)
      alert('画像生成に失敗しました')
    } finally {
      setIsGenerating(false)
    }
  }

  const handleGenerateStory = async () => {
    setIsGenerating(true)
    try {
      // テンプレートをInstagramストーリーの形式に変換
      const template = selectedTemplate === 'dark' ? 'bold' : (selectedTemplate === 'minimal' ? 'minimal' : 'gradient')
      const imageUrl = await generateInstagramStory(todo, template)
      setGeneratedImage(imageUrl)
    } catch (error) {
      console.error('Story generation failed:', error)
      alert('ストーリー生成に失敗しました')
    } finally {
      setIsGenerating(false)
    }
  }

  const handleShareToTwitter = () => {
    shareTaskCompletionToTwitter(todo)
  }

  const handleShareToFacebook = () => {
    const text = `✅ タスク完了！「${todo.title}」\n\n#TaStacK #タスク管理`
    shareToFacebook(text)
  }

  const handleShareToLinkedIn = () => {
    const text = `✅ タスク完了！「${todo.title}」\n\n#TaStacK #タスク管理`
    shareToLinkedIn(text)
  }

  const handleWebShare = async () => {
    const text = `✅ タスク完了！「${todo.title}」\n\n#TaStacK #タスク管理`
    const shared = await shareViaWebShareAPI({ text, hashtags: ['TaStacK', 'タスク管理'] })
    if (!shared) {
      alert('このブラウザはWeb Share APIをサポートしていません')
    }
  }

  const handleCopyText = async () => {
    const text = `✅ タスク完了！\n「${todo.title}」\n\n${todo.progress_percentage}% 達成\n\n#TaStacK #タスク管理 #生産性向上`
    const success = await copyToClipboard(text)
    if (success) {
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    }
  }

  const handleDownloadImage = () => {
    if (generatedImage) {
      downloadImage(generatedImage, `tastack-task-${todo.id}.png`)
    }
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 animate-fade-slide-in">
      <div className="bg-white dark:bg-gray-800 rounded-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto shadow-2xl animate-scale-in">
        {/* ヘッダー */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
          <h2 className="text-xl font-bold text-gray-900 dark:text-white">
            SNSでシェア
          </h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors tap-highlight"
          >
            <CloseIcon className="w-5 h-5 text-gray-500 dark:text-gray-400" />
          </button>
        </div>

        <div className="p-6 space-y-6">
          {/* タスク情報 */}
          <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
            <h3 className="font-semibold text-gray-900 dark:text-white mb-2">{todo.title}</h3>
            {todo.progress_percentage > 0 && (
              <div className="flex items-center gap-2">
                <div className="flex-1 bg-gray-200 dark:bg-gray-600 rounded-full h-2">
                  <div
                    className="bg-blue-600 h-2 rounded-full transition-all"
                    style={{ width: `${todo.progress_percentage}%` }}
                  />
                </div>
                <span className="text-sm text-gray-600 dark:text-gray-300">
                  {todo.progress_percentage}%
                </span>
              </div>
            )}
          </div>

          {/* クイックシェアボタン */}
          <div>
            <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">
              クイックシェア
            </h3>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <button
                onClick={handleShareToTwitter}
                className="flex flex-col items-center gap-2 p-4 border-2 border-gray-200 dark:border-gray-600 rounded-lg hover:border-blue-500 hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-all tap-highlight"
              >
                <Twitter className="w-6 h-6 text-blue-500" />
                <span className="text-xs font-medium text-gray-700 dark:text-gray-300">X (Twitter)</span>
              </button>

              <button
                onClick={handleShareToFacebook}
                className="flex flex-col items-center gap-2 p-4 border-2 border-gray-200 dark:border-gray-600 rounded-lg hover:border-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-all tap-highlight"
              >
                <Facebook className="w-6 h-6 text-blue-600" />
                <span className="text-xs font-medium text-gray-700 dark:text-gray-300">Facebook</span>
              </button>

              <button
                onClick={handleShareToLinkedIn}
                className="flex flex-col items-center gap-2 p-4 border-2 border-gray-200 dark:border-gray-600 rounded-lg hover:border-blue-700 hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-all tap-highlight"
              >
                <Linkedin className="w-6 h-6 text-blue-700" />
                <span className="text-xs font-medium text-gray-700 dark:text-gray-300">LinkedIn</span>
              </button>

              <button
                onClick={handleWebShare}
                className="flex flex-col items-center gap-2 p-4 border-2 border-gray-200 dark:border-gray-600 rounded-lg hover:border-gray-500 hover:bg-gray-50 dark:hover:bg-gray-600 transition-all tap-highlight"
              >
                <Share2 className="w-6 h-6 text-gray-600 dark:text-gray-300" />
                <span className="text-xs font-medium text-gray-700 dark:text-gray-300">その他</span>
              </button>
            </div>
          </div>

          {/* テンプレート選択 */}
          <div>
            <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">
              デザインテンプレート
            </h3>
            <div className="grid grid-cols-3 gap-3">
              <button
                onClick={() => setSelectedTemplate('minimal')}
                className={`p-4 border-2 rounded-lg transition-all ${
                  selectedTemplate === 'minimal'
                    ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                    : 'border-gray-200 dark:border-gray-600 hover:border-gray-300'
                }`}
              >
                <div className="w-full h-20 bg-white rounded mb-2 border border-gray-200" />
                <span className="text-xs font-medium text-gray-700 dark:text-gray-300">ミニマル</span>
              </button>

              <button
                onClick={() => setSelectedTemplate('colorful')}
                className={`p-4 border-2 rounded-lg transition-all ${
                  selectedTemplate === 'colorful'
                    ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                    : 'border-gray-200 dark:border-gray-600 hover:border-gray-300'
                }`}
              >
                <div className="w-full h-20 bg-gradient-to-br from-purple-500 to-pink-500 rounded mb-2" />
                <span className="text-xs font-medium text-gray-700 dark:text-gray-300">カラフル</span>
              </button>

              <button
                onClick={() => setSelectedTemplate('dark')}
                className={`p-4 border-2 rounded-lg transition-all ${
                  selectedTemplate === 'dark'
                    ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                    : 'border-gray-200 dark:border-gray-600 hover:border-gray-300'
                }`}
              >
                <div className="w-full h-20 bg-gray-900 rounded mb-2" />
                <span className="text-xs font-medium text-gray-700 dark:text-gray-300">ダーク</span>
              </button>
            </div>
          </div>

          {/* 画像生成ボタン */}
          <div className="grid grid-cols-2 gap-3">
            <button
              onClick={handleGenerateImage}
              disabled={isGenerating}
              className="flex items-center justify-center gap-2 px-4 py-3 bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-lg hover:from-blue-600 hover:to-blue-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed tap-highlight"
            >
              {isGenerating ? (
                <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
              ) : (
                <Download className="w-5 h-5" />
              )}
              <span className="font-medium">画像を生成</span>
            </button>

            <button
              onClick={handleGenerateStory}
              disabled={isGenerating}
              className="flex items-center justify-center gap-2 px-4 py-3 bg-gradient-to-r from-pink-500 to-purple-500 text-white rounded-lg hover:from-pink-600 hover:to-purple-600 transition-all disabled:opacity-50 disabled:cursor-not-allowed tap-highlight"
            >
              {isGenerating ? (
                <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
              ) : (
                <Instagram className="w-5 h-5" />
              )}
              <span className="font-medium">ストーリー生成</span>
            </button>
          </div>

          {/* 生成された画像のプレビュー */}
          {generatedImage && (
            <div className="space-y-3">
              <div className="relative bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                <img
                  src={generatedImage}
                  alt="Generated share image"
                  className="w-full rounded-lg"
                />
              </div>
              <button
                onClick={handleDownloadImage}
                className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors tap-highlight font-medium"
              >
                <Download className="w-5 h-5" />
                <span className="font-medium">画像をダウンロード</span>
              </button>
            </div>
          )}

          {/* テキストコピー */}
          <div>
            <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">
              テキストをコピー
            </h3>
            <button
              onClick={handleCopyText}
              className="w-full flex items-center justify-center gap-2 px-4 py-3 border-2 border-gray-200 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors tap-highlight"
            >
              {copied ? (
                <>
                  <Check className="w-5 h-5 text-green-600" />
                  <span className="font-medium text-green-600">コピーしました！</span>
                </>
              ) : (
                <>
                  <Copy className="w-5 h-5 text-gray-600 dark:text-gray-300" />
                  <span className="font-medium text-gray-700 dark:text-gray-200">シェア用テキストをコピー</span>
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
